import * as React from 'react';
import { Plus } from 'lucide-react';
import type { SectionSplit } from '@pageboss/shared';
import { SplitPopover } from './split-popover';
import { cn } from '@/lib/utils';

interface InsertSectionPointProps {
  // 'hover' renders an invisible-until-hover thin line; 'always' shows
  // a dashed pill (used at end-of-page for discoverability).
  variant?: 'hover' | 'always';
  label?: string;
  onPick: (split: SectionSplit) => void;
}

const ALL_SPLITS: readonly SectionSplit[] = ['full', '2-1', '1-2', '1-1', '1-1-1'];

export function InsertSectionPoint({
  variant = 'hover',
  label,
  onPick,
}: InsertSectionPointProps) {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);

  function openPicker() {
    const rect = triggerRef.current?.getBoundingClientRect() ?? null;
    setAnchorRect(rect);
  }
  function closePicker() {
    setAnchorRect(null);
  }
  function handlePick(split: SectionSplit) {
    closePicker();
    onPick(split);
  }

  return (
    <>
      {variant === 'always' ? (
        <div className="py-4 text-center">
          <button
            ref={triggerRef}
            type="button"
            onClick={openPicker}
            className={cn(
              'inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-dashed border-border bg-card px-4 py-2 text-sm font-medium text-text-secondary',
              'transition-colors hover:border-brand-500 hover:text-brand-700',
            )}
          >
            <Plus className="h-4 w-4" />
            {label ?? 'Add section'}
          </button>
        </div>
      ) : (
        <div
          className={cn(
            'group relative -my-1 flex h-5 items-center justify-center',
            'opacity-0 transition-opacity hover:opacity-100 focus-within:opacity-100',
          )}
        >
          <div className="absolute inset-x-12 top-1/2 h-px bg-border-subtle" aria-hidden />
          <button
            ref={triggerRef}
            type="button"
            onClick={openPicker}
            aria-label={label ?? 'Insert section here'}
            className={cn(
              'relative inline-flex items-center gap-1.5 rounded-full border border-brand-500 bg-card px-3 py-1 text-xs font-medium text-brand-700',
              'shadow-sm transition-transform hover:scale-105',
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Section</span>
          </button>
        </div>
      )}
      <SplitPopover
        open={anchorRect !== null}
        anchorRect={anchorRect}
        onClose={closePicker}
        onPick={handlePick}
        title="Add a row"
        options={ALL_SPLITS}
      />
    </>
  );
}
